import { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/company";

export const AuthProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Helper: get token/company from whichever storage has them
  const getStoredAuth = () => {
    // localStorage = "remember me", sessionStorage = session only
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const companyData =
      localStorage.getItem("company") || sessionStorage.getItem("company");
    return { token, companyData };
  };

  // Check token and user data on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { token, companyData } = getStoredAuth();

      if (token && companyData) {
        try {
          // Verify token is still valid
          const response = await fetch(`${baseUrl}/current`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const freshCompanyData = await response.json();
            setCompany(freshCompanyData);
            // Update in whichever storage was used
            if (localStorage.getItem("token")) {
              localStorage.setItem("company", JSON.stringify(freshCompanyData));
            } else {
              sessionStorage.setItem(
                "company",
                JSON.stringify(freshCompanyData),
              );
            }
          } else {
            // Token expired or invalid
            localStorage.removeItem("token");
            localStorage.removeItem("company");
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("company");
          }
        } catch (error) {
          console.error("Auth check failed:", error);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (token, companyData, rememberMe = false) => {
    // Clear both storages first
    localStorage.removeItem("token");
    localStorage.removeItem("company");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("company");

    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem("token", token);
    storage.setItem("company", JSON.stringify(companyData));
    setCompany(companyData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("company");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("company");
    setCompany(null);
    navigate("/login");
  };

  const updateCompanyContext = (newCompanyData) => {
    setCompany((prev) => {
      const updated = { ...prev, ...newCompanyData };
      // Update in whichever storage has the token
      if (localStorage.getItem("token")) {
        localStorage.setItem("company", JSON.stringify(updated));
      } else {
        sessionStorage.setItem("company", JSON.stringify(updated));
      }
      return updated;
    });
  };

  // Check if the current company is an admin
  const isAdmin = company?.isAdmin === 1;

  return (
    <AuthContext.Provider
      value={{ company, login, logout, loading, isAdmin, updateCompanyContext }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Helper: get auth token from whichever storage has it
export const getAuthToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token");
